import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import CustomButton from '../../component/ui/FormFields/Button';

const DocumentViewer = ({ fileUrl }) => {
  const [proofFileUrl, setProofFileUrl] = useState(null);
  const [proofFileName, setProofFileName] = useState(null);
  const [openDocumentPreview, setOpenDocumentPreview] = useState(false);
  const [fileType, setFileType] = useState('image');
  
  useEffect(() => {
    if (fileUrl) {
      setProofFileUrl(fileUrl);
      const fileName = fileUrl.split('/').pop();
      setProofFileName(fileName);
      
      // Determine file type based on extension
      const extension = fileName.split('.').pop().toLowerCase();
      if (extension === 'pdf') {
        setFileType('pdf');
      } else {
        setFileType('image');
      }
    }
  }, [fileUrl]);

  const handleOpenDocumentPreview = () => {
    if (!proofFileUrl) {
      toast.error('No document available for preview', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }
    setOpenDocumentPreview(true);
  };

  const handleCloseDocumentPreview = () => setOpenDocumentPreview(false);

  const handleDownloadDocument = () => {
    if (!proofFileUrl) {
      toast.error('No document available to download', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }

    const link = document.createElement('a');
    link.href = proofFileUrl;
    link.download = proofFileName || 'downloaded_document';
    link.target = '_blank'; // Open in new tab for better download experience
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderDocumentContent = () => {
    if (!proofFileUrl) return null;

    if (fileType === 'pdf') {
      return (
        <Box sx={{ width: '100%', height: '70vh' }}>
          <iframe
            src={proofFileUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title="PDF Preview"
          />
        </Box>
      );
    } else {
      return (
        <img
          src={proofFileUrl}
          alt="Proof Document"
          style={{ 
            maxWidth: '100%', 
            height: 'auto', 
            objectFit: 'contain',
            maxHeight: '70vh'
          }}
        />
      );
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Typography variant='subtitle1' className='role-table-fonts'>
        Proof Document
      </Typography>
      <Box
        className='proof-document-head'
        display='flex'
        alignItems='center'
        gap={2}
      >
        <Box display='flex' gap={2}>
          <CustomButton
            variant='contained'
            className='orange-button'
            startIcon={<VisibilityIcon />}
            onClick={handleOpenDocumentPreview}
            text='Preview'
          />
          <CustomButton
            variant='contained'
            className='grey-button'
            startIcon={<DownloadIcon />}
            onClick={handleDownloadDocument}
            text='Download'
          />
        </Box>
      </Box>
      <Dialog
        open={openDocumentPreview}
        onClose={handleCloseDocumentPreview}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>
          {fileType === 'pdf' ? 'PDF Preview' : 'Image Preview'}
          <IconButton
            aria-label='close'
            onClick={handleCloseDocumentPreview}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderDocumentContent()}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default DocumentViewer;